import React, { useEffect, useRef, useState } from "react";
import { MapPin, Calendar, Users, Search, Loader2, Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const field = "group flex min-w-0 flex-1 items-center gap-3 rounded-2xl bg-secondary/40 px-4 py-3 transition-colors duration-200 focus-within:bg-secondary/70 hover:bg-secondary/60";
const inputCls = "w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70 [color-scheme:dark] dark:[color-scheme:dark]";

function Counter({ label, value, min, max, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2" data-testid={testId}>
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary transition-colors hover:bg-accent/20 disabled:opacity-40" data-testid={`${testId}-minus`}><Minus className="h-3.5 w-3.5" /></button>
        <span className="w-4 text-center text-sm font-semibold" data-testid={`${testId}-value`}>{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary transition-colors hover:bg-accent/20 disabled:opacity-40" data-testid={`${testId}-plus`}><Plus className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export function HotelSearchBar({ value, onChange, onSearch, loading }) {
  const [query, setQuery] = useState(value.destination?.label || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const timer = useRef();

  useEffect(() => { setQuery(value.destination?.label || ""); }, [value.destination]);

  const lookup = (q) => {
    setQuery(q);
    onChange({ ...value, destination: null });
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setFetching(true);
      try {
        const res = await api.get("/travel/hotels/destinations", { params: { q } });
        setSuggestions(res.data); setOpen(true);
      } catch { setSuggestions([]); }
      finally { setFetching(false); }
    }, 300);
  };
  const pick = (s) => { onChange({ ...value, destination: s }); setQuery(s.label); setOpen(false); };
  const guests = `${value.adults} adult${value.adults > 1 ? "s" : ""}${value.children ? `, ${value.children} child${value.children > 1 ? "ren" : ""}` : ""} · ${value.rooms} room${value.rooms > 1 ? "s" : ""}`;
  const today = new Date().toISOString().slice(0, 10);
  const canSearch = value.destination && value.checkIn && value.checkOut && value.checkOut > value.checkIn;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (canSearch) onSearch(); }} className="rounded-3xl border border-border/60 bg-card p-3 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.35)]" data-testid="hotel-search-bar">
      <div className="flex flex-col gap-2 lg:flex-row">
        <div className="relative lg:flex-[1.6]">
          <div className={field}>
            <MapPin className="h-4 w-4 shrink-0 text-accent" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Where</p>
              <input value={query} onChange={(e) => lookup(e.target.value)} onFocus={() => suggestions.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="City or destination" className={inputCls} autoComplete="off" data-testid="hotel-destination-input" />
            </div>
            {fetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          {open && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-border/60 bg-card p-1.5 shadow-xl animate-fade-up" style={{ animationDuration: "200ms" }} data-testid="hotel-destination-suggestions">
              {suggestions.map((s) => (
                <button type="button" key={s.city_id} onMouseDown={() => pick(s)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-secondary/70" data-testid={`hotel-destination-option-${s.city_id}`}>
                  <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{s.label}</span>
                  {s.hotels > 0 && <span className="text-xs text-muted-foreground">{s.hotels.toLocaleString()} stays</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-1 gap-2">
          <label className={field}>
            <Calendar className="h-4 w-4 shrink-0 text-accent" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Check-in</p>
              <input type="date" min={today} value={value.checkIn} onChange={(e) => onChange({ ...value, checkIn: e.target.value })} className={inputCls} data-testid="hotel-checkin-input" />
            </div>
          </label>
          <label className={field}>
            <Calendar className="h-4 w-4 shrink-0 text-accent" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Check-out</p>
              <input type="date" min={value.checkIn || today} value={value.checkOut} onChange={(e) => onChange({ ...value, checkOut: e.target.value })} className={inputCls} data-testid="hotel-checkout-input" />
            </div>
          </label>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className={`${field} lg:flex-[0.9]`} data-testid="hotel-guests-trigger">
              <Users className="h-4 w-4 shrink-0 text-accent" />
              <div className="min-w-0 flex-1 text-left">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Guests</p>
                <p className="truncate text-sm">{guests}</p>
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 rounded-2xl border-border/60 p-4" data-testid="hotel-guests-popover">
            <Counter label="Adults" value={value.adults} min={1} max={8} onChange={(v) => onChange({ ...value, adults: v })} testId="guests-adults" />
            <Counter label="Children" value={value.children} min={0} max={6} onChange={(v) => onChange({ ...value, children: v })} testId="guests-children" />
            <Counter label="Rooms" value={value.rooms} min={1} max={4} onChange={(v) => onChange({ ...value, rooms: v })} testId="guests-rooms" />
          </PopoverContent>
        </Popover>
        <Button type="submit" disabled={!canSearch || loading} className="h-auto rounded-2xl px-6 py-3 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0" data-testid="hotel-search-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}<span className="ml-2">Search</span>
        </Button>
      </div>
    </form>
  );
}
